import React from 'react';
import { StyleSheet, ViewStyle } from 'react-native';

import { OneTimeTooltipGameTracker } from '@/components/OneTimeTooltipGameTracker';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { EventStatus } from '@/feature/bets-sbk/hooks/types';
import { OneTimeShowKeys } from '@/hooks/use-show-one-time';
import { common } from '@/styles/styles';
import { FloatingModalConfig } from '@/utils/floatingModal/FloatingModalProvider';

import { Accordion } from '../Accordion';
import { GameTrackerButton } from '../GameTrackerButton';
import MergedLogos from '../MergedLogos';
import { Text } from '../TextComponent';
import { Box, Row } from '../lib/components';
import { ds } from '../lib/design.system';
import { AppColors, getThemeColorHexValue } from '../lib/theme';
import { ScoreBoardGrid } from './ScoreBoardGrid';
import { ScoreBoardGridProps, TeamLogos } from './types';

export type ScoreBoardAccordionProps = {
    /** Output of `useScoreboard(event, 'card')` — undefined when the sport has no scoreboard builder. */
    scoreboardProps?: ScoreBoardGridProps;
    // ! background of the entire container but also the shadow's color
    backgroundColor?: AppColors;
    eventStatus: EventStatus;
    eventTitle: string;
    periodLabel?: string;
    /** Display-ordered per sport (getTeamOrder), matching scoreboardProps.team1/team2. */
    teamLogos: TeamLogos;
    teamFallbackTextColors?: string[];
    shouldShowFallbackLogos?: [boolean, boolean];
    accordionStyle?: ViewStyle;
    animateExplicitInitialExpansion?: boolean;
    gameTrackerConfig?: FloatingModalConfig;
    showGameTrackerTooltip?: boolean;
    screenName: OneTimeShowKeys;
    onViewFullEvent?: () => void;
};

export const ScoreBoardAccordion = ({
    backgroundColor = ds.colorToken.background.neutral.subtler,
    scoreboardProps,
    eventStatus,
    eventTitle,
    periodLabel,
    teamLogos,
    teamFallbackTextColors,
    shouldShowFallbackLogos,
    accordionStyle,
    animateExplicitInitialExpansion = false,
    gameTrackerConfig,
    showGameTrackerTooltip,
    screenName,
    onViewFullEvent,
}: ScoreBoardAccordionProps) => {
    const isLive = eventStatus === 'LIVE';
    const isEventFinished = eventStatus === 'FINISHED';
    const isAccordianEnabled = !!scoreboardProps?.breakdown?.length;
    const headerActiveOpacity = isLive && gameTrackerConfig && showGameTrackerTooltip ? 1 : undefined;
    const shouldInitiallyExpand = !!scoreboardProps && (isLive || isEventFinished || animateExplicitInitialExpansion);

    const handleToggleExpanded = (nextIsExpanded: boolean) => {
        if (nextIsExpanded) {
            BetrAnalytics.trackProductEvent(AnalyticsEvent.SCOREBOARD_EXPAND);
        } else {
            BetrAnalytics.trackProductEvent(AnalyticsEvent.SCOREBOARD_COLLAPSE);
        }
        return nextIsExpanded;
    };

    return (
        <Box paddingVertical="s10" backgroundColor={backgroundColor} borderRadius="r10" width="100%">
            <Accordion
                enabled={isAccordianEnabled}
                initialExpanded={shouldInitiallyExpand}
                accordionStyle={{ ...styles.accordion, ...accordionStyle }}
                headerStyle={styles.header}
                headerContainerStyle={gameTrackerConfig ? styles.headerContainer : undefined}
                contentStyle={styles.accordionContent}
                onToggle={handleToggleExpanded}
                header={
                    <Row alignItems="center" justifyContent="space-between">
                        <Row alignItems="center" flex={1}>
                            <Box style={styles.mergedLogoContainer}>
                                <MergedLogos
                                    borderColor={getThemeColorHexValue(backgroundColor)}
                                    logos={teamLogos}
                                    renderMode="shield"
                                    overlap={6}
                                    logoSizes={[16, 16]}
                                    backdropSizes={[28, 24]}
                                    imageShape="square"
                                    borderRadius={8}
                                    teamFallbackTextColors={teamFallbackTextColors}
                                    shouldShowFallbackLogos={shouldShowFallbackLogos}
                                />
                            </Box>
                            <Text
                                marginLeft="s12"
                                variant="bodySmall"
                                color={isLive ? ds.colorToken.text.primary : 'neutral300'}
                                numberOfLines={1}
                                ellipsizeMode="tail"
                                testID="betCardEventDetails"
                                style={styles.title}
                            >
                                {eventTitle}
                            </Text>
                            {periodLabel ? (
                                <Text variant="bodySmall" color={isLive ? ds.colorToken.text.primary : 'neutral300'}>
                                    {' • '}
                                    <Text variant="bodySmall" color={ds.colorToken.text.danger}>
                                        {periodLabel}
                                    </Text>
                                </Text>
                            ) : null}
                        </Row>
                        {(isEventFinished || isLive) && gameTrackerConfig ? (
                            <Box mr={isAccordianEnabled ? 's12' : 's0'}>
                                {showGameTrackerTooltip ? (
                                    <OneTimeTooltipGameTracker
                                        gameTrackerConfig={gameTrackerConfig}
                                        screenName={screenName}
                                        tooltipContentPosition={{ right: -14, top: 32 }}
                                    />
                                ) : (
                                    <GameTrackerButton config={gameTrackerConfig} />
                                )}
                            </Box>
                        ) : null}
                    </Row>
                }
                headerActiveOpacity={headerActiveOpacity}
                content={
                    scoreboardProps ? (
                        <ScoreBoardGrid
                            {...scoreboardProps}
                            backgroundColor={backgroundColor}
                            onViewFullEvent={onViewFullEvent}
                        />
                    ) : null
                }
            />
        </Box>
    );
};

const styles = StyleSheet.create({
    accordion: {
        backgroundColor: ds.colorValue.background.neutral.subtler,
    },
    header: {
        flexWrap: 'wrap',
        paddingHorizontal: 12,
        paddingVertical: 0,
        alignItems: 'center',
    },
    accordionContent: {
        paddingTop: 12,
        paddingBottom: 0,
        ...common.zIndexNegative1,
    },
    mergedLogoContainer: {
        marginLeft: -2,
    },
    title: {
        flexShrink: 1,
    },
    headerContainer: {
        ...common.zIndex1,
    },
});
